import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { SegmentTypeFilter } from "./SegmentTypeFilter"
import { PropertiesStore } from "./store"
import { IProperty } from "./types"

import { ListPage } from "src/components/ListPage"
import { Table } from "src/components/Table"
import { NoWrap } from "src/components/NoWrap"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { useInitializer } from "src/lib/initializer"

const View = observer(() => {
    const store = useStore(PropertiesStore)
    const globalStore = useStore(GlobalStore)
    const navigate = useNavigate()

    const initialized = useInitializer(async () => {
        await store.init(
            globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
        )
    }, [globalStore.session.accessGroup])

    const handleSearchChange = useCallback(
        (query: string) => {
            void store.properties.loadSearch(query)
        },
        [store],
    )

    const handleRowClick = useCallback(
        (item: IProperty) => {
            navigate(`/chatbot/properties/${item.id}`)
        },
        [navigate],
    )

    const header = useMemo(
        () => ({
            header: t`properties.header`,
            breadcrumbs: [t`navigation.chatbot`, t`properties.header`],
        }),
        [],
    )

    const filter = useMemo(
        () => ({
            query: store.properties.meta.search ?? "",
            onQueryChange: handleSearchChange,
            actions: <SegmentTypeFilter />,
        }),
        [handleSearchChange, store.properties.meta.search],
    )

    return (
        <ListPage header={header} filter={filter} loading={!initialized}>
            <Table
                paginator={store.properties}
                onRowClick={handleRowClick}
                headers={[
                    {
                        key: "id",
                        name: t`properties.column.id`,
                    },
                    {
                        key: "name",
                        name: t`properties.column.name`,
                    },
                ]}
                rowRenderer={(item) => ({
                    id: <NoWrap>{item.id}</NoWrap>,
                    name: <NoWrap title={item.name}>{item.name}</NoWrap>,
                })}
            />
        </ListPage>
    )
})

export const PropertiesView = () => (
    <StoreProvider Store={PropertiesStore}>
        <View />
    </StoreProvider>
)
